// The home screen's reads: what is asked, for whom, and when.
//
// Every number is its own read, so each lands when its own answer does and a refused or
// failed call leaves out only itself. Nothing is asked for a page or dashboard the
// person does not hold, and nothing at all until a project is selected.
import { useState } from 'react';
import {
  customDashboardsAPI, homeCountsAPI, moodDashboardAPI, redFlagDashboardAPI, rolesAPI, storeIntelligenceAPI,
  storeRequestsAPI, uniformDashboardAPI, yellowFlagDashboardAPI,
} from '../../services/api';
import type { DashTab } from '../../navigation/dashboards';
import type { DecisionCounts } from './decisions';
import {
  currentPeriodMonth, dashboardMonth, defaultRoleId, moderationWindow, readAbsentToday, readAppealsOpen,
  readApprovalsPending, readAttritionExits, readExitReviews, readFlaggedToday, readLowMood, readNeedsReview, readNoBuddy,
  readNoticeNotRaised, readOpenAppeals, readOpenReported, readPendingStoreRequests, readPlansToDecide, readProbationWaiting,
  readProjectRoles, readRequestsPending, readStoresShort, readStoresToFix, readTeamDashboards, readTrainingNotMarked,
  readUniformPastTarget, readYellowRaised, redAppealRows,
  type Kept, type KeptCount, type RequestsPending, type TeamDashboards,
} from './homeNumbers';
import {
  UNDECIDED, aiReviewWorklist, appealsDashboard, redAppealsWaiting, reportedPostsQueue, yellowAppealsWaiting,
} from './quietReads';
import { FAILED, LOADING, NOT_KNOWN_YET, OFF, useRead, type NotKnownYet, type Read } from './useRead';

/** A reader's null means the answer did not carry the number: the same as a failed call. */
function must<T>(value: T | null): T {
  if (value === null) throw new Error('The answer did not carry the number');
  return value;
}

/**
 * A kept count's answer, for useRead: the number, or "not known yet" (shown as no number
 * and asked for again shortly), or the failure every other missing number is.
 */
const kept = <T extends object>(answer: Kept<T>): T | NotKnownYet => (answer === 'not-yet' ? NOT_KNOWN_YET : must(answer));

/** One read for the selected project. `scope` names anything else the answer depends on. */
function useProjectRead<T>(
  name: string,
  projectId: number | null,
  wanted: boolean,
  load: (projectId: number) => Promise<T | NotKnownYet>,
  scope = '',
): Read<T> {
  return useRead(wanted && projectId !== null ? `${name}|${projectId}|${scope}` : null, () =>
    projectId === null ? Promise.reject(new Error('No project selected')) : load(projectId));
}

// ── Needs attention ──────────────────────────────────────────────────────────────

/**
 * The decision counts, every one asked here. (The Leave & Attendance Requests dashboard's
 * pending requests are a dashboard's number, asked with that dashboard's month and Role
 * filter beside the other dashboards' numbers: useDashboardNumbers. Since 22 Oct 2026 they
 * are no card of "Needs attention"; the Approvals card counts My Approvals instead.)
 */
export type ListCounts = DecisionCounts;

/** Which decision counts to ask for: true where the person holds the list the row opens. */
export type DecisionHolds = Record<keyof ListCounts, boolean>;

/** The counts behind the decision rows. Each is asked only when the person holds the list it opens. */
export function useDecisionCounts(projectId: number | null, holds: DecisionHolds): ListCounts {
  // Each appeals row is counted from the list it opens, asked for its two undecided
  // statuses. Both answers are needed: half a count would read as the whole.
  const redAppeals = useProjectRead('red-appeals', projectId, holds.redAppeals, async (pid) => {
    const answers = await Promise.all(UNDECIDED.map((status) => redAppealsWaiting({ projectId: pid, status })));
    return must(readOpenAppeals(answers.map((res) => redAppealRows(res.data?.data))));
  });

  const yellowAppeals = useProjectRead('yellow-appeals', projectId, holds.yellowAppeals, async (pid) => {
    const answers = await Promise.all(UNDECIDED.map((status) => yellowAppealsWaiting({ projectId: pid, status })));
    return must(readOpenAppeals(answers.map((res) => res.data?.data)));
  });

  // What waits on My Approvals for this person: kept by the server in the background, so
  // the first ask after a quiet spell may be "not known yet" (asked again, see useRead).
  const approvals = useProjectRead('approvals-pending', projectId, holds.approvals, async (pid) => {
    const res = await homeCountsAPI.approvalsPending(pid);
    return kept(readApprovalsPending(res.data?.data));
  });

  const exitReviews = useProjectRead('exit-reviews', projectId, holds.exitReviews, async (pid) => {
    const res = await homeCountsAPI.exitReviews(pid);
    return must(readExitReviews(res.data?.data));
  });

  const plansToDecide = useProjectRead('plans-to-decide', projectId, holds.plansToDecide, async (pid) => {
    const res = await homeCountsAPI.pipDecisions(pid);
    return must(readPlansToDecide(res.data?.data));
  });

  // The person's own queue across every project, so the key names no project: it is
  // asked once and kept through a project switch. Like the rest of the block it waits
  // for a project to be selected.
  const probation = useRead(holds.probation && projectId !== null ? 'probation' : null, async () => {
    const res = await homeCountsAPI.probation();
    return must(readProbationWaiting(res.data?.data));
  });

  const noBuddy = useProjectRead('no-buddy', projectId, holds.noBuddy, async (pid) => {
    const res = await homeCountsAPI.noBuddy(pid);
    return must(readNoBuddy(res.data?.data));
  });

  const storeRequests = useProjectRead('store-requests', projectId, holds.storeRequests, async (pid) => {
    const res = await storeRequestsAPI.summary(pid);
    return must(readPendingStoreRequests(res.data?.data));
  });

  // The Hiring dashboard's Notice period tab's count of the people whose hiring is not raised
  // yet: the tab's own read, answered as one number. A read that could not reach 1Office whole
  // for this person has no number here, and one made with no 1Office account to read with says
  // the tab lists nobody for them (readNoticeNotRaised). Given a short wait (HOME_NOTICE_WAIT_MS).
  const noticeHiring = useProjectRead('notice-hiring', projectId, holds.noticeHiring, async (pid) => {
    const res = await homeCountsAPI.noticeNotRaised(pid);
    return must(readNoticeNotRaised(res.data?.data));
  });

  // Two worklists on one page, each behind its own permission: either may be refused
  // while the other answers, so one failing does not cost the other its count.
  const posts = useProjectRead('posts', projectId, holds.posts, async (pid) => {
    const [check, reported] = await Promise.allSettled([
      aiReviewWorklist({ projectId: pid, filter: 'needs_review', limit: 1 }),
      reportedPostsQueue({ projectId: pid, status: 'open', ...moderationWindow() }),
    ]);
    const waiting = {
      fromCheck: check.status === 'fulfilled' ? readNeedsReview(check.value.data?.data) : null,
      reported: reported.status === 'fulfilled' ? readOpenReported(reported.value.data?.data) : null,
    };
    if (waiting.fromCheck === null && waiting.reported === null) throw new Error('Neither count arrived');
    return waiting;
  });

  return { redAppeals, yellowAppeals, approvals, exitReviews, plansToDecide, probation, noBuddy, storeRequests, posts, noticeHiring };
}

// ── Dashboards ───────────────────────────────────────────────────────────────────

export interface DashboardNumbers {
  /** Employees red-flagged today. */
  flaggedToday: Read<number>;
  /** Yellow flags raised this month. */
  yellowRaised: Read<number>;
  /** People unhappy on two or more days this month. */
  lowMood: Read<number>;
  /** Uniform requests past the 3-day target this month. */
  uniformPastTarget: Read<number>;
  /** Training invitations this month with attendance not marked. */
  trainingNotMarked: Read<number>;
  /** Leave and attendance requests raised this month and still undecided. Kept by the server. */
  requestsPending: Read<RequestsPending>;
  /** People who had not checked in though their shift had started. Kept by the server. */
  absentToday: Read<KeptCount>;
  /** Stores with nobody, or with too few people. Kept by the server. */
  storesShort: Read<KeptCount>;
  /** New joiners still waiting for a buddy. */
  noBuddy: Read<number>;
  /** Appeals nobody has decided yet, among the people the Appeals dashboard shows. */
  appealsOpen: Read<number>;
  /** Stores with something to fix on the Data Hygiene dashboard. */
  storesToFix: Read<number>;
  /** People who left this month, as the Attrition dashboard counts them. */
  attritionExits: Read<number>;
}

const UNIFORM: DashTab = 'Uniform & Asset Management';
const TRAINING: DashTab = 'Training';
const REQUESTS: DashTab = 'Approvals';
const ATTENDANCE: DashTab = 'Attendance';
const DEPLOYMENT: DashTab = 'Deployment';
const ONBOARDING: DashTab = 'Onboarding';
const APPEALS: DashTab = 'Appeals';
const HYGIENE: DashTab = 'Data Hygiene';
const ATTRITION: DashTab = 'Attrition';

/** A role-dependent number waits for the default roles: until they answer it is loading, and without them there is none. */
function behindRoles<T>(roles: Read<unknown>, wanted: boolean, read: Read<T>): Read<T> {
  if (!wanted || roles.status === 'off') return OFF;
  if (roles.status === 'loading') return LOADING;
  if (roles.status === 'failed') return FAILED;
  return read;
}

/**
 * The numbers on the dashboard tiles, for the dashboards in `tabs` only.
 *
 * Red Flag, Yellow Flag and Mood are asked with the month and `roleId` their dashboard
 * opens on and no other filter, through the dashboards' own calls, so each number is
 * the one the person sees on opening the dashboard. Training is asked the same way,
 * through a count of its own: its dashboard's call builds the whole dashboard. The
 * default roles come from the same two answers the Dashboard works them out from; if
 * either fails there is no role-dependent number at all, rather than one counted over
 * the wrong people. Uniform & Asset Management takes only the project: its one number is
 * the requests past the 3-day target, from the dashboard's own KPI call.
 *
 * Data Hygiene takes only the project too: that dashboard is always live, with no month,
 * and its Role filter narrows only the Employees side, so this stores-to-fix number takes
 * no role. Its number is a count of its own, worked out by the same checks as the
 * dashboard's Stores side. The server gives one only to someone who sees the whole
 * project; for anyone else the answer carries none, and the tile shows its name alone.
 *
 * Appeals is asked with the same month and `roleId` through the dashboard's one call,
 * which is small: the month's appeals and the open queue. Attrition is asked the same
 * way through a count of its own beside the dashboard's call (which also sends six
 * months of named leavers): the month's exits, for the same person and Role filter.
 * Score Movement has no number for the opposite reason: its one call builds the whole
 * dashboard, reading every scored person's name and reporting line, and no smaller call
 * counts the people still falling.
 *
 * `noBuddy` is handed in, not asked: the "Needs attention" card already counts
 * those joiners for whoever holds Onboarding, and one request serves the row and the tile.
 *
 * The last three are kept by the server in the background, and are asked the same way:
 * pending requests with the month and `roleId`, people absent today with `roleId` (that
 * list is always today's), stores short of people with the month and no `roleId` (that
 * list is not narrowed by role, so it does not wait for the roles either). The first
 * time a project is asked about the server has nothing kept: the answer is then "not
 * known yet", shown as no number, and asked for again shortly (see useRead).
 */
export function useDashboardNumbers(
  projectId: number | null,
  tabs: readonly DashTab[],
  noBuddy: Read<number>,
): DashboardNumbers {
  const [periodMonth] = useState(() => currentPeriodMonth());
  const month = dashboardMonth(periodMonth);
  const has = (tab: DashTab) => tabs.includes(tab);

  const needsRoles = has('Red Flag') || has('Yellow Flag') || has('Mood') || has(TRAINING) || has(APPEALS)
    || has(REQUESTS) || has(ATTENDANCE) || has(ATTRITION);
  const roles = useProjectRead('roles', projectId, needsRoles, async (pid) => {
    const [roleList, intelligence] = await Promise.all([
      rolesAPI.getByProject(pid),
      storeIntelligenceAPI.getSummary(pid, periodMonth, { monthEnd: true }),
    ]);
    // Boxed: "no Role filter" (undefined) is an answer too, not the lack of one.
    return { roleId: defaultRoleId(readProjectRoles(roleList.data?.data), intelligence.data?.data ?? null) };
  }, periodMonth);

  const known = roles.status === 'ready';
  const roleId = known ? roles.value.roleId : undefined;
  const scope = `${month}|${roleId ?? ''}`;

  const flaggedToday = useProjectRead('red-today', projectId, known && has('Red Flag'), async (pid) => {
    const res = await redFlagDashboardAPI.getToday({ projectId: pid, roleId });
    return must(readFlaggedToday(res.data?.data));
  }, scope);

  const yellowRaised = useProjectRead('yellow-raised', projectId, known && has('Yellow Flag'), async (pid) => {
    const res = await yellowFlagDashboardAPI.getTrend({ month, projectId: pid, roleId });
    return must(readYellowRaised(res.data?.data));
  }, scope);

  const lowMood = useProjectRead('low-mood', projectId, known && has('Mood'), async (pid) => {
    const res = await moodDashboardAPI.getWatchlist({ month, projectId: pid, roleId });
    return must(readLowMood(res.data?.data));
  }, scope);

  // The month is sent, never left to the server: the dashboard names it from the
  // browser's clock, and the two can differ around midnight on the last day.
  const trainingNotMarked = useProjectRead('training-not-marked', projectId, known && has(TRAINING), async (pid) => {
    const res = await homeCountsAPI.trainingNotMarked({ projectId: pid, month, roleId });
    return must(readTrainingNotMarked(res.data?.data));
  }, scope);

  const requestsPending = useProjectRead('requests-pending', projectId, known && has(REQUESTS), async (pid) => {
    const res = await homeCountsAPI.requestsPending({ projectId: pid, month, roleId });
    return kept(readRequestsPending(res.data?.data));
  }, scope);

  const absentToday = useProjectRead('absent-today', projectId, known && has(ATTENDANCE), async (pid) => {
    const res = await homeCountsAPI.absentToday({ projectId: pid, roleId });
    return kept(readAbsentToday(res.data?.data));
  }, scope);

  // The month is sent so the server can refuse a month that is not the one it keeps:
  // the two clocks can differ around midnight on the last day.
  const storesShort = useProjectRead('stores-short', projectId, has(DEPLOYMENT), async (pid) => {
    const res = await homeCountsAPI.storesShort({ projectId: pid, month });
    return kept(readStoresShort(res.data?.data));
  }, month);

  const appealsOpen = useProjectRead('appeals-open', projectId, known && has(APPEALS), async (pid) => {
    const res = await appealsDashboard({ month, projectId: pid, roleId });
    return must(readAppealsOpen(res.data?.data));
  }, scope);

  // The month is sent, as for Training: the dashboard names it from the browser's clock.
  const attritionExits = useProjectRead('attrition-exits', projectId, known && has(ATTRITION), async (pid) => {
    const res = await homeCountsAPI.attritionExits({ projectId: pid, month, roleId });
    return must(readAttritionExits(res.data?.data));
  }, scope);

  const uniformPastTarget = useProjectRead('uniform-target', projectId, has(UNIFORM), async (pid) => {
    const res = await uniformDashboardAPI.getKpi({ projectId: pid });
    return must(readUniformPastTarget(res.data?.data));
  });

  const storesToFix = useProjectRead('stores-to-fix', projectId, has(HYGIENE), async (pid) => {
    const res = await homeCountsAPI.dataHygiene(pid);
    return must(readStoresToFix(res.data?.data));
  });

  return {
    flaggedToday: behindRoles(roles, has('Red Flag'), flaggedToday),
    yellowRaised: behindRoles(roles, has('Yellow Flag'), yellowRaised),
    lowMood: behindRoles(roles, has('Mood'), lowMood),
    uniformPastTarget,
    trainingNotMarked: behindRoles(roles, has(TRAINING), trainingNotMarked),
    requestsPending: behindRoles(roles, has(REQUESTS), requestsPending),
    absentToday: behindRoles(roles, has(ATTENDANCE), absentToday),
    storesShort,
    noBuddy: has(ONBOARDING) ? noBuddy : OFF,
    appealsOpen: behindRoles(roles, has(APPEALS), appealsOpen),
    storesToFix,
    attritionExits: behindRoles(roles, has(ATTRITION), attritionExits),
  };
}

// ── Built by your team ───────────────────────────────────────────────────────────

/**
 * The dashboards the team built, and whether this person may build one: the same two
 * calls the Dashboard has always made together. A failure of either leaves the group out.
 */
export function useTeamDashboards(projectId: number | null): Read<TeamDashboards> {
  return useProjectRead('team-dashboards', projectId, true, async (pid) => {
    const [access, list] = await Promise.all([customDashboardsAPI.access(pid), customDashboardsAPI.list(pid)]);
    return readTeamDashboards(access.data, list.data);
  });
}
